import { useContext, useEffect, useMemo, useState } from "react"
import moment from "moment"
import firebase from "firebase"
import { X, Mail, Send, AlertTriangle } from "lucide-react"

import { db, auth, funEU } from "../../../../utils/firebase"
import { AlertsContext } from "../../../../utils/providers/alerts"
import { publishPublicQuote, publicQuoteUrl, buildSummaryLines, pickHeroImage } from "../../../../utils/quotes/public-quote"
import { FULL_BALANCE_WEEKS } from "../../../../utils/quotes/deposit"
import QuoteTerms from "../../../../components/quote-terms/quote-terms"
import Button from "../../../../components/ui/button/button"
import emailHeaderLogo from "../../../../assets/images/ghd-email-header.png"

import "./send-quote-email.scss"

/**
 * Quote email modal, opened from the Send action on a draft quote card.
 *
 * Sends a branded "quote_link" email - a short note plus a "View Quote" button -
 * to the client, FROM the sending agent's own @golfholidaysdirect.com address
 * (per-user From, resolved server-side by the sendEnquiryEmail callable). The
 * modal is a faithful preview of that email; the body is templated, so there are
 * no editable quote fields here for now.
 */

const sendEnquiryEmailCallable = funEU.httpsCallable("sendEnquiryEmail")

// Fallback validity if the channel picker somehow didn't supply one.
const DEFAULT_VALID_FOR = { amount: 24, unit: "hours" }


const fullName = (data) => [data?.first_name, data?.last_name].filter(Boolean).join(" ").trim()

const initialsFor = (name) =>
	(name || "")
		.split(" ")
		.filter(Boolean)
		.map((part) => part[0])
		.join("")
		.slice(0, 2)
		.toUpperCase() || "GHD"

// GHDQ-XXXXXX, matching the quoting engine's reference format (quoting/context).
const generateQuoteRef = () => {
	const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
	let result = "GHDQ-"
	for (let i = 0; i < 6; i++) result += chars.charAt(Math.floor(Math.random() * chars.length))
	return result
}

// Hotel name + travel month/year for the email body, derived from the draft.
const deriveQuoteFields = (draft) => {
	const basket = Array.isArray(draft?.basket) ? draft.basket : []
	const hotels = basket.filter((item) => item.type === "hotel")
	// Distinct titles: a hotel added twice (e.g. a package + a supplement) is one
	// stay, so it names the quote once - matching the merged itinerary.
	const hotelName = [...new Set(hotels.map((item) => item.title).filter(Boolean))].join(", ")
	const travelLabel = draft?.travel_millis ? moment(draft.travel_millis).format("MMMM YYYY") : ""
	return { hotelName, travelLabel }
}

// Stripped-back "ticket" for the email: the hero image, a one-line trip-facts
// summary (date · nights · golfers) and the "what's included" lines. No price -
// the per-person figures live on the online quote behind the View Quote button.
const deriveTicket = (draft) => {
	const basket = Array.isArray(draft?.basket) ? draft.basket : []
	const groupSize = Number(draft?.group_size) || 0
	const tripFacts = [
		draft?.travel_millis ? moment(draft.travel_millis).format("ddd D MMM YYYY") : "",
		draft?.nights ? `${draft.nights} night${draft.nights === 1 ? "" : "s"}` : "",
		groupSize ? `${groupSize} golfer${groupSize === 1 ? "" : "s"}` : "",
	]
		.filter(Boolean)
		.join("  ·  ")

	// Clarifying footnote shown under the "Quote valid until" line (and on the
	// public page): the per-person price(s) are calculated for the whole party,
	// so spell out the group size. "Price is" vs "Prices are" tracks whether the
	// quote is split into priced tiers (sharing / single occupancy).
	const splitTiers = Boolean(draft?.sell_split) && Array.isArray(draft?.sell_lines) ? draft.sell_lines.filter((line) => (Number(line.price) || 0) > 0) : []
	const priceBasisNote = groupSize > 0 ? `${splitTiers.length > 1 ? "Prices are" : "Price is"} based on ${groupSize} ${groupSize === 1 ? "person" : "people"}.` : ""

	return { heroImage: pickHeroImage(basket), tripFacts, summaryLines: buildSummaryLines(basket), priceBasisNote }
}

export default function SendQuoteEmailModal({ draft, enquiryID, validFor, onClose, onSent }) {
	const { pushAlert } = useContext(AlertsContext)
	const { hotelName, travelLabel } = useMemo(() => deriveQuoteFields(draft), [draft])
	const { heroImage, tripFacts, summaryLines, priceBasisNote } = useMemo(() => deriveTicket(draft), [draft])

	// Reference: reuse the quote doc's if it already has one, else mint a fresh
	// GHDQ- code (persisted to the doc on send).
	const [reference] = useState(() => draft?.reference || generateQuoteRef())

	// Validity chosen in the channel picker (amount + unit), fixed at modal open so
	// the preview and the sent email agree on the exact time.
	// The quote's expiry, fixed at modal open from the chosen validity. Stored on
	// the published quote (the public page hides all book / secure buttons past
	// it) and shown to the client as the "valid until" date - the two stay in
	// step, both derived from this single instant.
	const expiresAtMillis = useMemo(() => {
		const { amount, unit } = validFor || DEFAULT_VALID_FOR
		return moment().add(amount, unit).valueOf()
	}, [validFor])

	// "Quote only": when set, the published page hides every book/secure button
	// and points the client at their sales agent instead.
	const bookDisabled = Boolean(validFor?.bookDisabled)
	// Discount keys ("{itemUid}:{discountId}") the agent unticked on the send
	// screen - hidden from the public quote (display-only; price is unaffected).
	// Persisted on the quote doc so a later copy-link re-publish keeps the choice.
	const hiddenDiscounts = useMemo(() => (Array.isArray(validFor?.hiddenDiscounts) ? validFor.hiddenDiscounts : []), [validFor])
	// Full-balance threshold (weeks before travel) chosen in the channel picker;
	// falls back to the standard 12 weeks. Persisted on the quote + public doc so
	// the public page and the authoritative server charge both honour it.
	const fullBalanceWeeks = Number(validFor?.fullBalanceWeeks) > 0 ? Number(validFor.fullBalanceWeeks) : FULL_BALANCE_WEEKS
	const validUntilLabel = useMemo(() => moment(expiresAtMillis).format("D MMMM YYYY [at] h:mma"), [expiresAtMillis])

	const [clientName, setClientName] = useState("")
	// The enquiry's linked client - the quote email goes here.
	const [recipient, setRecipient] = useState({ name: "", email: "" })
	const [sender, setSender] = useState({ name: "", email: "", phone: "", photo: "" })

	// The public "View Quote" URL. Seeded from an already-published token (if the
	// quote has been sent before) and refreshed once we re-publish on open.
	const [quoteUrl, setQuoteUrl] = useState(() => publicQuoteUrl(draft?.public_token))

	const [loadingContext, setLoadingContext] = useState(true)
	const [sending, setSending] = useState(false)
	const [error, setError] = useState("")

	// Deliberately NO Escape / outside-click close: the quote modals only close
	// via the X or their action buttons, so a prepared send can't be lost.

	// Resolve the client (greeting) + the sending agent (From + sales-agent card).
	useEffect(() => {
		let cancelled = false

		const loadContext = async () => {
			try {
				const enquirySnap = await db.doc(`enquiries/${enquiryID}`).get()
				const enquiry = enquirySnap.data() || {}

				let client = {}
				if (enquiry.client) {
					const clientSnap = await db.doc(`clients/${enquiry.client}`).get()
					client = clientSnap.data() || {}
				}

				let senderData = {}
				if (auth.currentUser?.uid) {
					const senderSnap = await db.doc(`users/${auth.currentUser.uid}`).get()
					senderData = senderSnap.data() || {}
				}

				if (cancelled) return
				setClientName(client.first_name || "")
				setRecipient({ name: fullName(client), email: String(client.email || "").trim() })
				setSender({
					name: fullName(senderData) || auth.currentUser?.displayName || "",
					email: senderData.email || auth.currentUser?.email || "",
					phone: senderData.phone || senderData.telephone || "",
					// Same photo precedence the public quote uses for the agent card.
					photo: senderData.profilePicture?._256 || senderData.profilePicture?._528 || senderData.profilePicture?.original || senderData.picture || "",
				})
			} catch (err) {
				console.error("Could not load email context:", err)
			} finally {
				if (!cancelled) setLoadingContext(false)
			}
		}

		loadContext()
		return () => {
			cancelled = true
		}
	}, [enquiryID])

	// Publish (or refresh) the client-facing public quote page so the email's
	// "View Quote" link resolves to the real /quote/{token} page. Best-effort on
	// open; handleSend re-publishes if this hasn't produced a URL yet.
	useEffect(() => {
		let cancelled = false
		if (!enquiryID || !draft?.id) return undefined

		publishPublicQuote(enquiryID, { ...draft, reference, expires_at: expiresAtMillis, book_disabled: bookDisabled, full_balance_weeks: fullBalanceWeeks, hidden_discounts: hiddenDiscounts })
			.then(({ url }) => {
				if (!cancelled) setQuoteUrl(url)
			})
			.catch((err) => {
				console.error("Could not publish public quote:", err)
			})

		return () => {
			cancelled = true
		}
	}, [enquiryID, draft, reference, expiresAtMillis, bookDisabled, fullBalanceWeeks, hiddenDiscounts])

	const subject = `Your Golf Holiday Quote - Golf Holidays Direct [${reference}]`
	const greeting = clientName ? `Hi ${clientName},` : "Hi there,"
	const intro = `Please find below a link to view your golf holiday quote${hotelName ? ` to ${hotelName}` : ""}${
		travelLabel ? ` in ${travelLabel}` : ""
	}. Take a read through and don't hesitate to let me know if there is anything at all you would like me to revisit.`

	const senderLabel = sender.email ? `${sender.name || "You"} <${sender.email}>` : sender.name || "your account"
	// "To" label, and a guard so we never send to a blank address - the email goes
	// to the enquiry's client, not a test inbox.
	const recipientLabel = recipient.email ? `${recipient.name ? `${recipient.name} ` : ""}<${recipient.email}>` : ""
	const hasRecipient = Boolean(recipient.email)
	const canSend = !sending && !loadingContext && hasRecipient

	const handleSend = async () => {
		if (!canSend) return
		setSending(true)
		setError("")

		// Make sure the public page is live and we have its URL before the email
		// goes out (the on-open publish may still be in flight, or have failed).
		let url = quoteUrl
		if (!url) {
			try {
				url = (await publishPublicQuote(enquiryID, { ...draft, reference, expires_at: expiresAtMillis, book_disabled: bookDisabled, full_balance_weeks: fullBalanceWeeks, hidden_discounts: hiddenDiscounts })).url
				setQuoteUrl(url)
			} catch (err) {
				console.error("Could not publish public quote:", err)
				setError("Could not prepare the quote link. Please try again.")
				setSending(false)
				return
			}
		}

		try {
			await sendEnquiryEmailCallable({
				enquiryID,
				template: "quote_link",
				recipientEmail: recipient.email,
				recipientName: recipient.name || undefined,
				hotelName: hotelName || undefined,
				travelLabel: travelLabel || undefined,
				quoteUrl: url,
				validUntil: validUntilLabel,
				priceBasisNote: priceBasisNote || undefined,
				balanceWeeks: fullBalanceWeeks,
				reference,
				// Stripped-back ticket (hero image + trip facts + what's included, no price).
				heroImage: heroImage || undefined,
				tripFacts: tripFacts || undefined,
				summaryLines: summaryLines.length ? summaryLines : undefined,
			})

			// The quote has now gone out: stamp the reference and flip it from a
			// draft to an active/sent quote so it moves into the Sent quotes section.
			if (draft?.id) {
				await db.doc(`enquiries/${enquiryID}/quotes/${draft.id}`).set(
					{
						reference,
						is_draft: false,
						is_active: true,
						sent_at: firebase.firestore.FieldValue.serverTimestamp(),
						// Expiry the public page enforces; persisted so a later copy-link
						// re-publish carries it.
						expires_at: expiresAtMillis,
						// Quote-only flag, persisted so a re-publish keeps the page in
						// quote-only mode.
						book_disabled: bookDisabled,
						// Full-balance threshold (weeks); the authoritative deposit
						// charge (createQuoteDepositIntent) reads this off the quote.
						full_balance_weeks: fullBalanceWeeks,
						// Display-only: discounts the agent chose NOT to advertise on the
						// public quote. Persisted so a copy-link re-publish keeps the choice.
						hidden_discounts: hiddenDiscounts,
					},
					{ merge: true },
				)
			}

			pushAlert({ type: "SUCCESS", title: "Quote sent", body: `Sent to ${recipient.email}.` })
			if (onSent) onSent()
			onClose()
		} catch (err) {
			console.error("Could not send quote email:", err)
			setError(err?.message || "The email could not be sent. Please try again.")
			setSending(false)
		}
	}

	return (
		<div className="send-quote-overlay">
			<div className="send-quote-modal" role="dialog" aria-modal="true" aria-label="Send quote email">
				<div className="send-quote-header">
					<div className="send-quote-header-title">
						<Mail size={18} />
						<h3>Email this quote</h3>
					</div>
					<button type="button" className="send-quote-close" onClick={() => !sending && onClose()} aria-label="Close">
						<X size={18} />
					</button>
				</div>

				<div className="send-quote-meta">
							<MetaRow label="To" value={loadingContext ? "Loading…" : recipientLabel || "No client email on file"} />
							<MetaRow label="From" value={senderLabel} />
							<MetaRow label="Subject" value={subject} />
						</div>

						{/* Faithful preview of the templated email (mirrors the backend
						    quoteLinkTemplate - keep the two in step). */}
						<div className="send-quote-preview-area">
							<div className="sqe-email">
								<div className="sqe-email-header">
									<img src={emailHeaderLogo} alt="Golf Holidays Direct" />
								</div>

								<div className="sqe-email-body">
									<p className="sqe-greeting">{greeting}</p>
									<p className="sqe-intro">{intro}</p>

									{(heroImage || tripFacts || summaryLines.length > 0) && (
										<div className="sqe-ticket">
											{heroImage && (
												<div className="sqe-ticket-media">
													<img src={heroImage} alt={hotelName || "Your golf holiday"} />
												</div>
											)}
											<div className="sqe-ticket-body">
												{hotelName && <p className="sqe-ticket-title">{hotelName}</p>}
												{tripFacts && <p className="sqe-ticket-facts">{tripFacts}</p>}
												{summaryLines.length > 0 && (
													<ul className="sqe-ticket-lines">
														{summaryLines.map((line, index) => (
															<li key={index}>
																<span className="sqe-ticket-bullet">&bull;</span>
																<span>{line.text}</span>
															</li>
														))}
													</ul>
												)}
											</div>
										</div>
									)}

									<div className="sqe-cta">
										<a className="sqe-button" href={quoteUrl || undefined} target="_blank" rel="noreferrer">
											View Quote
										</a>
										<p className="sqe-valid">Quote valid until {validUntilLabel}</p>
										{priceBasisNote ? <p className="sqe-price-basis">{priceBasisNote}</p> : null}
									</div>

									<div className="sqe-agent-card">
										<div className="sqe-agent-avatar">
											{sender.photo ? <img src={sender.photo} alt={sender.name || "Your sales agent"} /> : initialsFor(sender.name)}
										</div>
										<div className="sqe-agent-text">
											<span className="sqe-agent-label">Your sales agent</span>
											<span className="sqe-agent-name">{sender.name || "Golf Holidays Direct"}</span>
											{sender.email && <span className="sqe-agent-line is-link">{sender.email}</span>}
											{sender.phone && <span className="sqe-agent-line">{sender.phone}</span>}
										</div>
									</div>

									<QuoteTerms className="sqe-terms" balanceWeeks={fullBalanceWeeks} />
								</div>

								<div className="sqe-email-footer">Have any questions or would like to change something? Please reply to this email.</div>
							</div>
						</div>

						<div className="send-quote-footer">
							{!loadingContext && !hasRecipient && !error && (
								<p className="send-quote-error">
									<AlertTriangle size={14} />
									<span>This enquiry's client has no email on file - add one before sending.</span>
								</p>
							)}
							{error && (
								<p className="send-quote-error">
									<AlertTriangle size={14} />
									<span>{error}</span>
								</p>
							)}
							<div className="send-quote-footer-actions">
								<Button type="secondary" label="Cancel" onClick={() => !sending && onClose()} />
								<Button
									label="Send email"
									icon={<Send size={15} />}
									loading={sending}
									loadingText="Sending..."
									disabled={!canSend}
									onClick={handleSend}
								/>
							</div>
						</div>
			</div>
		</div>
	)
}

function MetaRow({ label, value }) {
	return (
		<div className="send-quote-meta-row">
			<span className="send-quote-meta-label">{label}</span>
			<span className="send-quote-meta-value">{value}</span>
		</div>
	)
}
